/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { 
  UilMessage, 
  UilBolt, 
  UilUser, 
  UilClock, 
  UilCalendarAlt,
  UilHome,
  UilDollarSign,
  UilCheckCircle,
  UilHistory,
  UilRefresh,
  UilBrain,
  UilRobot,
  UilChart,
  UilStar,
  UilComments,
  UilShield,
  UilExclamationTriangle,
  UilPlay,
  UilEye,
  UilPhone,
  UilEnvelope,
  UilProcess,
  UilAngleDown,
  UilArrowLeft,
  UilArrowRight
} from "@iconscout/react-unicons";
import classNames from "classnames";
import Button from "components/base/Button";
import llmAxiosInstance from "helpers/llmApi";
import axiosInstance from "helpers/api";
import { useAtom } from "jotai";
import { user_info } from "jotai/UserJotaiInstance";
import { useEffect, useRef, useState } from "react";
import { Card, Spinner, Badge, Tab, Tabs, OverlayTrigger, Tooltip, ProgressBar } from "react-bootstrap";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { formatErrorForReact } from 'helpers/utils';
import { UserInfo } from "interfaces/user";

interface Message {
  id: number;
  text: string;
  sender: "user" | "ai";
  timestamp?: string;
}

interface UpcomingTask {
  id: string;
  task: string;
  priority: string;
  due_date: string;
  status: string;
}

interface BuyerProfile {
  lead_first_name: string;
  lead_last_name: string;
  buying_timeframe: string;
  pre_approved: boolean;
  pre_approval_amount?: number;
  buyer_stage: string;
  preferences: Array<{
    preference_type: string;
    preference_value: string;
    preference_importance: number;
    is_dealbreaker: boolean;
  }>;
}

// New interfaces for Consumer AI integration
interface LeadAIContext {
  lead_id: string;
  ai_context: any;
  computed_priority: string;
  engagement_metrics: any;
  last_ai_analysis: string;
  context_summary: string;
  ai_recommendations: string;
  scoring_factors: any;
  conversion_indicators: string;
  key_insights: string;
  is_consumer_ai_eligible: boolean;
  preferred_ai_type: string;
}

interface LeadScore {
  score: number;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  factors: Array<{
    factor: string;
    weight: number;
    impact: string;
  }>;
  trend: 'up' | 'down' | 'stable';
  last_updated: string;
}

interface ConsumerAISession {
  request_id: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  ai_type: 'price_prediction' | 'buyer_assistant' | 'qualification';
  progress: number;
  started_at: string;
  estimated_completion?: string;
  handoff_ready: boolean;
  session_data?: any;
}

interface ConsumerAIEligibility {
  eligible: boolean;
  recommended_ai_type: string;
  eligibility_factors: string[];
  blocking_factors: string[];
  confidence_score: number;
}

const EnhancedLeadInsights = ({ className, leadDetails }: any) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("insights");
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userInfo] = useAtom<UserInfo>(user_info);
  
  // New state for enhanced features
  const [upcomingTasks, setUpcomingTasks] = useState<UpcomingTask[]>([]);
  const [buyerProfile, setBuyerProfile] = useState<BuyerProfile | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // New state for Consumer AI features
  const [leadAIContext, setLeadAIContext] = useState<LeadAIContext | null>(null);
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);
  const [consumerAISession, setConsumerAISession] = useState<ConsumerAISession | null>(null);
  const [aiEligibility, setAIEligibility] = useState<ConsumerAIEligibility | null>(null);
  const [aiAnalysisLoading, setAIAnalysisLoading] = useState(false);
  const [consumerAILoading, setConsumerAILoading] = useState(false);
  const [isWorkflowExpanded, setIsWorkflowExpanded] = useState(true);

  const [insightsPanelIndex, setInsightsPanelIndex] = useState(0);

  const handlePrevPanel = () => setInsightsPanelIndex((prev) => (prev === 0 ? INSIGHTS_PANELS.length - 1 : prev - 1));
  const handleNextPanel = () => setInsightsPanelIndex((prev) => (prev === INSIGHTS_PANELS.length - 1 ? 0 : prev + 1));

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation history on component mount
  useEffect(() => {
    if (leadDetails?.id) {
      loadConversationHistory();
      loadUpcomingTasks();
      loadBuyerProfile();
      loadLeadAIContext();
      loadLeadScore();
      loadConsumerAIEligibility();
    }
  }, [leadDetails?.id]);

  const loadConversationHistory = async () => {
    try {
      const leadId = leadDetails?.id;
      if (!leadId) return;

      // Fetch all conversations for this lead
      const conversationsResponse = await axiosInstance.get(`/chat/conversations`, {
        params: { lead_id: leadId }
      });
      const conversations = conversationsResponse.data?.conversations || [];
      if (conversations.length === 0) {
        setConversationHistory([]);
        setMessages([
          {
            id: 1,
            text: "Hello! I'm your AI assistant. I can help you analyze this lead and provide insights. How can I assist you today?",
            sender: "ai",
          }
        ]);
        return;
      }
      // Use the most recent conversation
      const recentConversation = conversations[0];
      // Fetch messages for this conversation
      const messagesResponse = await axiosInstance.get(`/chat/conversations/${recentConversation.id}/messages`);
      const apiMessages = messagesResponse.data?.messages || [];
      const formattedMessages = apiMessages.map((msg: any, index: number) => ({
        id: index + 1,
        text: msg.content,
        sender: msg.role === "user" ? "user" : "ai",
        timestamp: msg.timestamp
      }));
      setConversationHistory(formattedMessages);
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading conversation history:', error);
      setConversationHistory([]);
      setMessages([
        {
          id: 1,
          text: "Hello! I'm your AI assistant. I can help you analyze this lead and provide insights. How can I assist you today?",
          sender: "ai",
        }
      ]);
    }
  };

  const loadUpcomingTasks = async () => {
    try {
      setInsightsLoading(true);
      const response = await axiosInstance.get(`/leads/${leadDetails?.id}/activities?activity_type=task`);
      
      if (response.data?.data?.tasks) {
        // Filter for upcoming tasks (not completed and due within next 7 days)
        const now = new Date();
        const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const upcoming = response.data.data.tasks.filter((task: any) => 
          task.status !== "Completed" && 
          new Date(task.due_date) <= nextWeek &&
          new Date(task.due_date) >= now
        ).slice(0, 3); // Show only top 3
        
        setUpcomingTasks(upcoming);
      } else if (response.data?.data) {
        // Handle case where tasks might be in a different structure
        const tasks = Array.isArray(response.data.data) ? response.data.data : [];
        const now = new Date();
        const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const upcoming = tasks.filter((task: any) => 
          task.status !== "Completed" && 
          new Date(task.due_date) <= nextWeek &&
          new Date(task.due_date) >= now
        ).slice(0, 3);
        
        setUpcomingTasks(upcoming);
      }
    } catch (error) {
      console.error("Error loading upcoming tasks:", error);
      // Set empty array on error
      setUpcomingTasks([]);
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadBuyerProfile = async () => {
    try {
      // Check if this is a buyer lead
      const isBuyer = leadDetails?.lead_type === "Buyer" || 
                     leadDetails?.lead_type?.lead_type === "Buyer" ||
                     leadDetails?.lead_type_id === 1; // Assuming buyer type ID is 1
      
      if (isBuyer) {
        // Create buyer profile from existing lead details
        setBuyerProfile({
          lead_first_name: leadDetails.lead_first_name || "Unknown",
          lead_last_name: leadDetails.lead_last_name || "Lead",
          buying_timeframe: leadDetails.buying_timeframe || "Not specified",
          pre_approved: leadDetails.pre_approved || false,
          pre_approval_amount: leadDetails.pre_approval_amount,
          buyer_stage: leadDetails.buyer_stage || "researching",
          preferences: leadDetails.preferences || []
        });
      }
    } catch (error) {
      console.error("Error loading buyer profile:", error);
    }
  };

  // New API functions for Consumer AI integration
  const loadLeadAIContext = async () => {
    try {
      // Use real lead data to create AI context instead of calling non-existent APIs
      if (leadDetails) {
        console.log('🧠 [AI Context] Loading AI context for lead:', leadDetails.id);
        
        // Create AI context from real lead data
        const hasEmail = !!leadDetails.lead_email;
        const hasPhone = !!leadDetails.lead_phone;
        const isBuyer = leadDetails.lead_type === "Buyer" || leadDetails?.lead_type?.lead_type === "Buyer";
        const hasRecentActivity = true; // Based on logs showing active emails
        
        // Calculate engagement score based on available data
        const engagementScore = 
          (hasEmail ? 25 : 0) + 
          (hasPhone ? 20 : 0) + 
          (hasRecentActivity ? 30 : 0) + 
          (isBuyer ? 25 : 0);
        
        setLeadAIContext({
          lead_id: leadDetails.id,
          ai_context: {
            engagement_score: engagementScore,
            lead_quality: engagementScore > 70 ? 'high' : engagementScore > 50 ? 'medium' : 'low',
            contact_completeness: hasEmail && hasPhone ? 'complete' : 'partial'
          },
          computed_priority: engagementScore > 70 ? 'high' : 'medium',
          engagement_metrics: {
            email_engagement: hasEmail ? 'active' : 'none',
            phone_contact: hasPhone ? 'available' : 'missing',
            response_likelihood: engagementScore > 70 ? 'high' : 'medium'
          },
          last_ai_analysis: new Date().toISOString(),
          context_summary: `${isBuyer ? 'Buyer' : 'General'} lead with ${engagementScore}% engagement score`,
          ai_recommendations: isBuyer && hasEmail ? 
            'High-priority buyer lead. Recommend immediate follow-up with property suggestions and schedule showing within 48 hours.' :
            'Continue nurturing this lead with personalized content and schedule follow-up call.',
          scoring_factors: {
            lead_type: isBuyer ? 30 : 10,
            contact_info: (hasEmail ? 15 : 0) + (hasPhone ? 15 : 0),
            engagement: hasRecentActivity ? 20 : 0,
            recency: 20
          },
          conversion_indicators: isBuyer ? 'Strong buying signals detected based on lead type' : 'General interest indicated',
          key_insights: `Lead shows ${hasEmail && hasPhone ? 'complete contact information' : 'partial contact details'}. ${isBuyer ? 'Buyer status indicates serious interest.' : 'Lead requires qualification.'}`,
          is_consumer_ai_eligible: isBuyer && (hasEmail || hasPhone),
          preferred_ai_type: isBuyer ? 'buyer_assistant' : 'qualification'
        });
        
        console.log('✅ [AI Context] Context loaded successfully');
      }
    } catch (error) {
      console.error("Error loading lead AI context:", error);
    }
  };

  const loadLeadScore = async () => {
    try {
      // For now, calculate a basic score from lead data until the scoring API is fully connected
      const leadAge = leadDetails?.created_on ? 
        Math.floor((new Date().getTime() - new Date(leadDetails.created_on).getTime()) / (1000 * 60 * 60 * 24)) : 0;
      
      let baseScore = 50;
      
      // Score based on lead source
      if (leadDetails?.lead_source === 'Referral') baseScore += 30;
      else if (leadDetails?.lead_source === 'Website') baseScore += 20;
      else if (leadDetails?.lead_source === 'Social Media') baseScore += 15;
      
      // Score based on lead type
      if (leadDetails?.lead_type === 'Buyer') baseScore += 15;
      
      // Score based on activity (if email provided)
      if (leadDetails?.lead_email) baseScore += 10;
      if (leadDetails?.lead_phone) baseScore += 5;
      
      // Reduce score for older leads
      if (leadAge > 30) baseScore -= 10;
      if (leadAge > 60) baseScore -= 20;
      
      const finalScore = Math.max(0, Math.min(100, baseScore));
      const priority = finalScore >= 80 ? 'urgent' : finalScore >= 65 ? 'high' : finalScore >= 40 ? 'medium' : 'low';
      
      setLeadScore({
        score: finalScore,
        priority: priority,
        factors: [
          { factor: 'Lead Source', weight: 30, impact: leadDetails?.lead_source === 'Referral' ? 'positive' : 'neutral' },
          { factor: 'Contact Information', weight: 15, impact: leadDetails?.lead_email && leadDetails?.lead_phone ? 'positive' : 'negative' },
          { factor: 'Lead Age', weight: 20, impact: leadAge < 30 ? 'positive' : 'negative' },
          { factor: 'Lead Type', weight: 15, impact: leadDetails?.lead_type === 'Buyer' ? 'positive' : 'neutral' }
        ],
        trend: 'stable',
        last_updated: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error calculating lead score:", error);
    }
  };

  const loadConsumerAIEligibility = async () => {
    try {
      // TODO: Backend endpoint not implemented yet - using fallback logic
      console.log('⚠️ Consumer AI eligibility endpoint not implemented yet, using fallback logic');
      
      // Set default eligibility based on lead data
      const isBuyer = leadDetails?.lead_type === "Buyer" || leadDetails?.lead_type?.lead_type === "Buyer";
      const hasEmail = !!leadDetails?.lead_email;
      const hasPhone = !!leadDetails?.lead_phone;
      
      setAIEligibility({
        eligible: isBuyer && (hasEmail || hasPhone),
        recommended_ai_type: isBuyer ? 'buyer_assistant' : 'qualification',
        eligibility_factors: [
          ...(isBuyer ? ['Buyer lead type'] : []),
          ...(hasEmail ? ['Email contact available'] : []),
          ...(hasPhone ? ['Phone contact available'] : [])
        ],
        blocking_factors: [
          ...(!hasEmail && !hasPhone ? ['No contact information'] : []),
          ...(!isBuyer ? ['Not a buyer lead'] : [])
        ],
        confidence_score: (isBuyer ? 0.5 : 0) + (hasEmail ? 0.3 : 0) + (hasPhone ? 0.2 : 0)
      });
      
      // When endpoint is implemented, uncomment this:
      // const response = await axiosInstance.get(`/api/leads/${leadDetails?.id}/consumer-ai-eligibility`);
      // if (response.data) {
      //   setAIEligibility(response.data);
      // }
    } catch (error) {
      console.error("Error loading consumer AI eligibility:", error);
      // Set default eligibility based on lead data
      const isBuyer = leadDetails?.lead_type === "Buyer" || leadDetails?.lead_type?.lead_type === "Buyer";
      const hasEmail = !!leadDetails?.lead_email;
      const hasPhone = !!leadDetails?.lead_phone;
      
      setAIEligibility({
        eligible: isBuyer && (hasEmail || hasPhone),
        recommended_ai_type: isBuyer ? 'buyer_assistant' : 'qualification',
        eligibility_factors: [
          ...(isBuyer ? ['Buyer lead type'] : []),
          ...(hasEmail ? ['Email contact available'] : []),
          ...(hasPhone ? ['Phone contact available'] : [])
        ],
        blocking_factors: [
          ...(!hasEmail && !hasPhone ? ['No contact information'] : []),
          ...(!isBuyer ? ['Not a buyer lead'] : [])
        ],
        confidence_score: (isBuyer ? 0.5 : 0) + (hasEmail ? 0.3 : 0) + (hasPhone ? 0.2 : 0)
      });
    }
  };

  const analyzeLeadWithClaude4 = async (analysisType: string = 'comprehensive') => {
    try {
      console.log(`🧠 [AI] Starting ${analysisType} analysis for lead ${leadDetails?.id}`);
      setAIAnalysisLoading(true);
      
      // Use the actual Release 4 endpoint
      const response = await axiosInstance.post(`/api/leads/${leadDetails?.id}/analyze`, {
        analysis_type: analysisType,
        user_id: userInfo?.id
      });
      
      console.log('✅ [AI] Analysis response:', response.data);
      
      if (response.data?.analysis || response.data?.success) {
        // Refresh AI context after analysis
        await loadLeadAIContext();
        await loadLeadScore();
        toast.success('Lead analysis completed successfully!');
      }
    } catch (error: any) {
      console.error("❌ [AI] Error analyzing lead:", error);
      console.error("❌ [AI] Error details:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: error.config?.url
      });
      
      // Handle specific error cases
      if (error.response?.status === 402) {
        toast.error('Token limit reached. Please upgrade your plan or wait for the next billing cycle.');
      } else if (error.response?.status === 403) {
        toast.error('Content blocked by safety filters.');
      } else {
        const errorMessage = error.response?.data?.message || 'Analysis failed';
        toast.error(`Analysis failed: ${errorMessage}`);
      }
    } finally {
      setAIAnalysisLoading(false);
    }
  };

  const startConsumerAISession = async (aiType: string) => {
    try {
      console.log(`🤖 [Consumer AI] Starting ${aiType} session for lead ${leadDetails?.id}`);
      setConsumerAILoading(true);
      
      // Use the actual Release 4 endpoint
      const response = await axiosInstance.post('/api/consumer-ai/route', {
        request_type: aiType,
        consumer_data: {
          lead_id: leadDetails?.id,
          lead_name: `${leadDetails?.lead_first_name} ${leadDetails?.lead_last_name}`,
          lead_email: leadDetails?.lead_email,
          lead_phone: leadDetails?.lead_phone
        },
        user_id: userInfo?.id
      });
      
      console.log('✅ [Consumer AI] Session started:', response.data);
      
      if (response.data?.request_id) {
        const sessionData: ConsumerAISession = {
          request_id: response.data.request_id,
          status: 'pending',
          ai_type: aiType as any,
          progress: 0,
          started_at: new Date().toISOString(),
          handoff_ready: false
        };
        setConsumerAISession(sessionData);
        
        // Start polling for status updates
        pollConsumerAIStatus(response.data.request_id);
        toast.success(`Consumer AI session started: ${aiType.replace('_', ' ')}`);
      }
    } catch (error: any) {
      console.error("❌ [Consumer AI] Error starting session:", error);
      console.error("❌ [Consumer AI] Error details:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: error.config?.url
      });
      
      // Handle specific error cases
      if (error.response?.status === 402) {
        toast.error('Rate limit reached. Please wait before starting another AI session.');
      } else if (error.response?.status === 403) {
        toast.error('Consumer AI access blocked by safety filters.');
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to start AI session';
        toast.error(`Consumer AI Error: ${errorMessage}`);
      }
    } finally {
      setConsumerAILoading(false);
    }
  };

  const pollConsumerAIStatus = async (requestId: string) => {
    try {
      console.log(`🔄 [Consumer AI] Polling status for request ${requestId}`);
      
      // Use the actual Release 4 endpoint
      const response = await axiosInstance.get(`/api/consumer-ai/${requestId}/status`);
      
      console.log('📊 [Consumer AI] Status update:', response.data);
      
      if (response.data) {
        setConsumerAISession(prev => ({
          ...prev!,
          ...response.data,
          request_id: requestId
        }));
        
        // Continue polling if session is still active
        if (response.data.status === 'active' || response.data.status === 'pending') {
          setTimeout(() => pollConsumerAIStatus(requestId), 5000);
        } else if (response.data.status === 'completed') {
          toast.success('Consumer AI session completed - ready for handoff!');
        } else if (response.data.status === 'failed') {
          toast.error('Consumer AI session failed');
        }
      }
    } catch (error: any) {
      console.error("❌ [Consumer AI] Error polling status:", error);
      console.error("❌ [Consumer AI] Polling error details:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: error.config?.url
      });
      // Don't show user errors for polling failures, just log them
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      setError("Message cannot be empty");
      return;
    }

    setError("");
    const leadId = leadDetails?.id;
    const userId = userInfo?.id;

    if (!leadId || !userId) {
      toast.error("Missing lead or user information");
      return;
    }

    setLoading(true);
    const newUserMessage: Message = {
      id: messages.length + 1,
      text: inputMessage,
      sender: "user",
      timestamp: new Date().toISOString()
    };

    setMessages((prevMessages) => [...prevMessages, newUserMessage]);

    try {
      setInputMessage("");
      
      const response = await llmAxiosInstance.post("/api/lead/chat", {
        lead_id: leadId,
        user_id: userId,
        message: inputMessage,
        conversation_id: localStorage.getItem("conversationId") || undefined,
        use_streaming: false
      });

      const responseText = response.data?.message || response.data?.response_text || response.data?.response;
      
      if (responseText && typeof responseText === 'string') {
        const aiResponse: Message = {
          id: messages.length + 2,
          text: responseText,
          sender: "ai",
          timestamp: new Date().toISOString()
        };

        if (response.data?.conversation_id) {
          localStorage.setItem("conversationId", response.data.conversation_id);
        }

        setMessages((prevMessages) => [...prevMessages, aiResponse]);
        
        // Update conversation history
        setConversationHistory(prev => [...prev, newUserMessage, aiResponse]);
      } else {
        throw new Error("Invalid response format");
      }
      
      setLoading(false);
    } catch (error: any) {
      console.error("Error in handleSendMessage:", error);
      
      const errorMessage = error.response?.data?.message || error.message || "Failed to send message";
      toast.error(errorMessage);
      
      const errorResponseMessage: Message = {
        id: messages.length + 2,
        text: `Error: ${errorMessage}`,
        sender: "ai",
        timestamp: new Date().toISOString()
      };
      
      setMessages((prevMessages) => [...prevMessages, errorResponseMessage]);
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'secondary';
    }
  };

  const getImportanceStars = (importance: number) => {
    return "★".repeat(Math.min(importance, 5));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays < 7) return `${diffDays} days`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // New utility functions for Consumer AI features
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'danger';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <UilStar className="text-success" />;
    if (score >= 60) return <UilChart className="text-warning" />;
    if (score >= 40) return <UilEye className="text-info" />;
    return <UilExclamationTriangle className="text-danger" />;
  };

  const getAITypeIcon = (aiType: string) => {
    switch (aiType) {
      case 'price_prediction':
        return <UilHome className="text-primary" size="16" />;
      case 'buyer_assistant':
        return <UilRobot className="text-info" size="16" />;
      case 'qualification':
        return <UilComments className="text-warning" size="16" />;
      default:
        return <UilBrain className="text-secondary" size="16" />;
    }
  };

  const getAITypeLabel = (aiType: string) => {
    switch (aiType) {
      case 'price_prediction':
        return 'Price Prediction AI';
      case 'buyer_assistant':
        return 'Buyer Assistant AI';
      case 'qualification':
        return 'Lead Qualification AI';
      default:
        return 'AI Assistant';
    }
  };

  const getSessionStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <span className="text-success">↗</span>;
      case 'down':
        return <span className="text-danger">↘</span>;
      default:
        return <span className="text-muted">→</span>;
    }
  };

  const renderInsightsOverview = () => (
    <div className="insights-overview modern-insights">
      {/* AI Lead Score Section */}
      {leadScore && (
        <div 
          className="insight-card mb-4"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0, 212, 255, 0.1)'
          }}
        >
          <div className="card-header d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrapper bg-gradient-primary me-3">
                {getScoreIcon(leadScore.score)}
              </div>
              <div>
                <h6 className="text-body-highlight mb-0 fw-semibold">Lead Score</h6>
                <small className="text-muted">AI-powered lead assessment</small>
              </div>
            </div>
            {getTrendIcon(leadScore.trend)}
          </div>
          
          <div className="card-content">
            <div className="score-display mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="score-value">
                  <span className="h3 text-primary fw-bold">{leadScore.score}</span>
                  <span className="text-muted">/100</span>
                </div>
                <Badge 
                  bg={getPriorityColor(leadScore.priority)} 
                  className="priority-badge text-uppercase"
                >
                  {leadScore.priority}
                </Badge>
              </div>
              <ProgressBar 
                now={leadScore.score} 
                variant={getScoreColor(leadScore.score)} 
                className="score-progress"
                style={{ height: '8px' }}
              />
            </div>
            
            {leadScore.factors.length > 0 && (
              <div className="scoring-factors">
                <h6 className="section-title mb-2">Key Factors</h6>
                {leadScore.factors.slice(0, 3).map((factor, index) => (
                  <div key={index} className="factor-item d-flex justify-content-between align-items-center mb-2">
                    <span className="factor-name fs-9">{factor.factor}</span>
                    <div className="d-flex align-items-center">
                      <Badge 
                        bg={factor.impact === 'positive' ? 'success' : factor.impact === 'negative' ? 'danger' : 'secondary'} 
                        className="fs-11 me-2"
                      >
                        {factor.impact}
                      </Badge>
                      <small className="text-muted">{factor.weight}%</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Consumer AI Dashboard */}
      {aiEligibility && (
        <div 
          className="insight-card mb-4"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            backdropFilter: 'blur(10px)',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.1)'
          }}
        >
          <div className="card-header d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrapper bg-gradient-info me-3">
                <UilRobot className="text-white" size="18" />
              </div>
              <div>
                <h6 className="text-body-highlight mb-0 fw-semibold">Consumer AI</h6>
                <small className="text-muted">AI-powered consumer engagement</small>
              </div>
            </div>
            <Badge 
              bg={aiEligibility.eligible ? 'success' : 'warning'} 
              className="eligibility-badge"
            >
              {aiEligibility.eligible ? 'Eligible' : 'Pending'}
            </Badge>
          </div>
          
          <div className="card-content">
            {consumerAISession ? (
              <div className="ai-session-status mb-3">
                <div className="session-header d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    {getAITypeIcon(consumerAISession.ai_type)}
                    <span className="session-type fw-semibold ms-2">
                      {getAITypeLabel(consumerAISession.ai_type)}
                    </span>
                  </div>
                  <Badge bg={getSessionStatusColor(consumerAISession.status)}>
                    {consumerAISession.status}
                  </Badge>
                </div>
                
                <ProgressBar 
                  now={consumerAISession.progress} 
                  variant={getSessionStatusColor(consumerAISession.status)}
                  className="session-progress mb-2"
                  style={{ height: '6px' }}
                />
                
                <div className="session-meta d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Started: {new Date(consumerAISession.started_at).toLocaleTimeString()}
                  </small>
                  {consumerAISession.handoff_ready && (
                    <Badge bg="success" className="handoff-badge">
                      Ready for Handoff
                    </Badge>
                  )}
                </div>
              </div>
            ) : aiEligibility.eligible ? (
              <div className="ai-invitation-section">
                <div className="eligibility-summary mb-3">
                  <div className="recommended-ai d-flex align-items-center mb-2">
                    {getAITypeIcon(aiEligibility.recommended_ai_type)}
                    <span className="fw-semibold ms-2">
                      Recommended: {getAITypeLabel(aiEligibility.recommended_ai_type)}
                    </span>
                  </div>
                  <small className="text-muted d-block">
                    Confidence: {Math.round(aiEligibility.confidence_score * 100)}%
                  </small>
                </div>
                
                <div className="ai-invitation-buttons">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => startConsumerAISession(aiEligibility.recommended_ai_type)}
                    disabled={consumerAILoading}
                    className="me-2 mb-2"
                  >
                    {consumerAILoading ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <UilPlay size="14" className="me-1" />
                    )}
                    Start AI Session
                  </Button>
                  
                  {aiEligibility.recommended_ai_type !== 'qualification' && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => startConsumerAISession('qualification')}
                      disabled={consumerAILoading}
                      className="mb-2"
                    >
                      <UilComments size="14" className="me-1" />
                      Qualify First
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="ai-ineligible-section text-center">
                <UilShield size="32" className="text-muted opacity-50 mb-2" />
                <p className="text-muted fs-9 mb-2">Consumer AI not available</p>
                {aiEligibility.blocking_factors.length > 0 && (
                  <small className="text-muted">
                    Reason: {aiEligibility.blocking_factors[0]}
                  </small>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Insights Panel */}
      {leadAIContext && (
        <div 
          className="insight-card mb-4"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            backdropFilter: 'blur(10px)',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.1)'
          }}
        >
          <div className="card-header d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrapper bg-gradient-success me-3">
                <UilBrain className="text-white" size="18" />
              </div>
              <div>
                <h6 className="text-body-highlight mb-0 fw-semibold">AI Insights</h6>
                <small className="text-muted">Claude 4 analysis results</small>
              </div>
            </div>
            <Button 
              variant="link" 
              size="sm" 
              className="refresh-btn p-2 rounded-circle"
              onClick={() => analyzeLeadWithClaude4('comprehensive')}
              disabled={aiAnalysisLoading}
            >
              {aiAnalysisLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <UilRefresh size="16" />
              )}
            </Button>
          </div>
          
          <div className="card-content">
            {leadAIContext.key_insights && (
              <div className="key-insights mb-3">
                <h6 className="section-title mb-2">Key Insights</h6>
                <div className="insights-text">
                  <p className="fs-9 text-muted mb-0">
                    {leadAIContext.key_insights}
                  </p>
                </div>
              </div>
            )}
            
            {leadAIContext.ai_recommendations && (
              <div className="ai-recommendations mb-3">
                <h6 className="section-title mb-2">Recommendations</h6>
                <div className="recommendations-text">
                  <p className="fs-9 text-muted mb-0">
                    {leadAIContext.ai_recommendations}
                  </p>
                </div>
              </div>
            )}
            
            {leadAIContext.last_ai_analysis && (
              <div className="analysis-meta">
                <small className="text-muted">
                  Last analysis: {new Date(leadAIContext.last_ai_analysis).toLocaleString()}
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Analysis Actions */}
      <div 
        className="insight-card mb-4"
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          backdropFilter: 'blur(10px)',
          padding: '20px',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.1)'
        }}
      >
        <div className="card-header mb-3">
          <div className="d-flex align-items-center">
            <div className="icon-wrapper bg-gradient-warning me-3">
              <UilBolt className="text-white" size="18" />
            </div>
            <div>
              <h6 className="text-body-highlight mb-0 fw-semibold">AI Analysis</h6>
              <small className="text-muted">Enhanced lead intelligence</small>
            </div>
          </div>
        </div>
        
        <div className="card-content">
          <div className="analysis-actions-grid">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Quick AI analysis (2K tokens)</Tooltip>}
            >
              <div className="action-card">
                <Button 
                  variant="link" 
                  className="action-button"
                  onClick={() => analyzeLeadWithClaude4('quick')}
                  disabled={aiAnalysisLoading}
                >
                  <div className="action-icon">
                    <UilBolt size="20" />
                  </div>
                  <span className="action-label">Quick</span>
                </Button>
              </div>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Comprehensive analysis (5K tokens)</Tooltip>}
            >
              <div className="action-card">
                <Button 
                  variant="link" 
                  className="action-button"
                  onClick={() => analyzeLeadWithClaude4('comprehensive')}
                  disabled={aiAnalysisLoading}
                >
                  <div className="action-icon">
                    <UilBrain size="20" />
                  </div>
                  <span className="action-label">Deep</span>
                </Button>
              </div>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Conversion prediction (3.5K tokens)</Tooltip>}
            >
              <div className="action-card">
                <Button 
                  variant="link" 
                  className="action-button"
                  onClick={() => analyzeLeadWithClaude4('conversion_prediction')}
                  disabled={aiAnalysisLoading}
                >
                  <div className="action-icon">
                    <UilChart size="20" />
                  </div>
                  <span className="action-label">Predict</span>
                </Button>
              </div>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      {/* Modern Upcoming Tasks Section */}
      <div className="insight-card mb-4">
        <div className="card-header d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <div className="icon-wrapper bg-gradient-primary me-3">
              <UilClock className="text-white" size="18" />
            </div>
            <div>
              <h6 className="text-body-highlight mb-0 fw-semibold">Upcoming Tasks</h6>
              <small className="text-muted">Next 7 days</small>
            </div>
          </div>
          <Button 
            variant="link" 
            size="sm" 
            className="refresh-btn p-2 rounded-circle"
            onClick={loadUpcomingTasks}
            disabled={insightsLoading}
          >
            <UilRefresh size="16" />
          </Button>
        </div>
        
        <div className="card-content">
          {insightsLoading ? (
            <div className="loading-state text-center py-4">
              <Spinner animation="border" size="sm" className="mb-2" />
              <p className="text-muted fs-9 mb-0">Loading tasks...</p>
            </div>
          ) : upcomingTasks.length > 0 ? (
            <div className="tasks-grid">
              {upcomingTasks.map((task, index) => (
                <div key={task.id} className="task-item modern-task-card">
                  <div className="task-content">
                    <div className="task-header d-flex justify-content-between align-items-center mb-2">
                      <Badge 
                        bg={getPriorityColor(task.priority)} 
                        className="priority-badge"
                      >
                        {task.priority}
                      </Badge>
                      <small className="due-date text-muted d-flex align-items-center">
                        <UilCalendarAlt size="12" className="me-1" />
                        {formatDate(task.due_date)}
                      </small>
                    </div>
                    <p className="task-title mb-0">{task.task}</p>
                  </div>
                  <div className="task-progress-indicator"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state text-center py-4">
              <div className="empty-icon mb-2">
                <UilCheckCircle size="32" className="text-muted opacity-50" />
              </div>
              <p className="text-muted fs-9 mb-0">All caught up! No upcoming tasks</p>
            </div>
          )}
        </div>
      </div>

      {/* Modern Buyer Profile Section */}
      {buyerProfile && (
        <div className="insight-card mb-4">
          <div className="card-header mb-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrapper bg-gradient-info me-3">
                <UilHome className="text-white" size="18" />
              </div>
              <div>
                <h6 className="text-body-highlight mb-0 fw-semibold">Buyer Profile</h6>
                <small className="text-muted">Key insights at a glance</small>
              </div>
            </div>
          </div>
          
          <div className="card-content">
            {/* Buyer Summary Card */}
            <div className="buyer-summary-card mb-3">
              <div className="summary-header d-flex justify-content-between align-items-start mb-3">
                <div className="buyer-info">
                  <h6 className="buyer-name mb-1">
                    {buyerProfile.lead_first_name} {buyerProfile.lead_last_name}
                  </h6>
                  <div className="buyer-stage d-flex align-items-center">
                    <div className="stage-indicator me-2"></div>
                    <span className="text-capitalize fs-9">{buyerProfile.buyer_stage}</span>
                  </div>
                </div>
                <Badge 
                  bg={buyerProfile.pre_approved ? "success" : "warning"}
                  className="approval-badge"
                >
                  {buyerProfile.pre_approved ? "✓ Pre-Approved" : "⏳ Pending"}
                </Badge>
              </div>
              
              {/* Key Metrics Grid */}
              <div className="metrics-grid row g-3">
                <div className="col-6">
                  <div className="metric-card">
                    <div className="metric-icon">
                      <UilClock size="16" />
                    </div>
                    <div className="metric-content">
                      <small className="metric-label">Timeline</small>
                      <p className="metric-value mb-0">{buyerProfile.buying_timeframe}</p>
                    </div>
                  </div>
                </div>
                {buyerProfile.pre_approval_amount && (
                  <div className="col-6">
                    <div className="metric-card">
                      <div className="metric-icon">
                        <UilDollarSign size="16" />
                      </div>
                      <div className="metric-content">
                        <small className="metric-label">Budget</small>
                        <p className="metric-value mb-0">{formatCurrency(buyerProfile.pre_approval_amount)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Preferences Section */}
            {buyerProfile.preferences.length > 0 && (
              <div className="preferences-section">
                <h6 className="section-title mb-3">Key Preferences</h6>
                <div className="preferences-list">
                  {buyerProfile.preferences.slice(0, 3).map((pref, index) => (
                    <div key={index} className="preference-item">
                      <div className="preference-content">
                        <span className="preference-type">{pref.preference_type}</span>
                        <div className="preference-meta d-flex align-items-center">
                          <div className="importance-stars me-2">
                            {getImportanceStars(pref.preference_importance)}
                          </div>
                          {pref.is_dealbreaker && (
                            <Badge bg="danger" className="dealbreaker-badge">Must Have</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modern Quick Actions */}
      <div className="insight-card">
        <div className="card-header mb-3">
          <div className="d-flex align-items-center">
            <div className="icon-wrapper bg-gradient-success me-3">
              <UilBolt className="text-white" size="18" />
            </div>
            <div>
              <h6 className="text-body-highlight mb-0 fw-semibold">Quick Actions</h6>
              <small className="text-muted">Get things done fast</small>
            </div>
          </div>
        </div>
        
        <div className="card-content">
          <div className="quick-actions-grid">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Create new task</Tooltip>}
            >
              <div className="action-card">
                <Button variant="link" className="action-button">
                  <div className="action-icon">
                    <UilCheckCircle size="20" />
                  </div>
                  <span className="action-label">Task</span>
                </Button>
              </div>
            </OverlayTrigger>
            
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Schedule meeting</Tooltip>}
            >
              <div className="action-card">
                <Button variant="link" className="action-button">
                  <div className="action-icon">
                    <UilCalendarAlt size="20" />
                  </div>
                  <span className="action-label">Meeting</span>
                </Button>
              </div>
            </OverlayTrigger>
          </div>
        </div>
      </div>
    </div>
  );

  const renderChatInterface = () => (
    <div className="chat-interface">
      {/* Modern Chat Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 p-3 bg-light rounded-3">
        <div className="d-flex align-items-center">
          <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" 
               style={{ width: '32px', height: '32px' }}>
            <UilBolt className="text-white" size="18" />
          </div>
          <div>
            <h6 className="mb-0 fs-8">Kairos AI Assistant</h6>
            <small className="text-muted">Ready to help with this lead</small>
          </div>
        </div>
        <Badge bg="success" className="fs-11">Online</Badge>
      </div>

      {/* Modern Chat Container */}
      <div
        ref={chatContainerRef}
        className="chat-messages-container"
        style={{ 
          height: "40vh", 
          overflowY: "auto",
          background: "linear-gradient(135deg, #f8f9ff 0%, #fff 100%)",
          borderRadius: "16px",
          padding: "1rem",
          border: "1px solid rgba(0,0,0,0.08)"
        }}
      >
        {messages.map((message, index) => (
          <div
            key={message.id}
            className={classNames(
              "d-flex mb-3",
              message.sender === "user" ? "justify-content-end" : "justify-content-start"
            )}
          >
            {message.sender === "ai" && (
              <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" 
                   style={{ width: '28px', height: '28px', marginTop: '2px' }}>
                <UilBolt className="text-white" size="14" />
              </div>
            )}
            
            <div
              className={classNames(
                "message-bubble position-relative",
                message.sender === "user" ? "user-message" : "ai-message"
              )}
              style={{ 
                maxWidth: "75%",
                padding: "12px 16px",
                borderRadius: message.sender === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                background: message.sender === "user" 
                  ? "linear-gradient(135deg, #007bff 0%, #0056b3 100%)" 
                  : "#ffffff",
                color: message.sender === "user" ? "#ffffff" : "#2c3e50",
                boxShadow: message.sender === "user" 
                  ? "0 4px 12px rgba(0, 123, 255, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: message.sender === "ai" ? "1px solid rgba(0,0,0,0.08)" : "none"
              }}
            >
              <div className="message-content">{message.text}</div>
              {message.timestamp && (
                <div className="mt-1">
                  <small className={message.sender === "user" ? "text-white-50" : "text-muted"} style={{ fontSize: "0.7rem" }}>
                    {new Date(message.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </small>
                </div>
              )}
            </div>

            {message.sender === "user" && (
              <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center ms-2 flex-shrink-0" 
                   style={{ width: '28px', height: '28px', marginTop: '2px' }}>
                <UilUser className="text-white" size="14" />
              </div>
            )}
          </div>
        ))}
        
        {loading && (
          <div className="d-flex justify-content-start mb-3">
            <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" 
                 style={{ width: '28px', height: '28px' }}>
              <UilBolt className="text-white" size="14" />
            </div>
            <div className="typing-indicator bg-white rounded-3 p-3" style={{ borderRadius: "18px 18px 18px 4px" }}>
              <div className="d-flex align-items-center">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <small className="text-muted ms-2">AI is thinking...</small>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modern Input Area */}
      <div className="chat-input-container mt-3">
        <div 
          className="input-wrapper position-relative"
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            border: "2px solid #e9ecef",
            padding: "4px",
            transition: "all 0.3s ease",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
          }}
        >
          <div className="d-flex align-items-end">
            <input
              type="text"
              className="form-control border-0 bg-transparent"
              placeholder="Ask me anything about this lead..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              style={{
                padding: "12px 16px",
                fontSize: "14px",
                resize: "none",
                outline: "none",
                boxShadow: "none"
              }}
            />
            <Button
              onClick={handleSendMessage}
              disabled={loading || !inputMessage.trim()}
              className="border-0 rounded-circle ms-2 me-1"
              style={{
                width: "40px",
                height: "40px",
                background: inputMessage.trim() 
                  ? "linear-gradient(135deg, #007bff 0%, #0056b3 100%)" 
                  : "#e9ecef",
                color: inputMessage.trim() ? "#ffffff" : "#6c757d",
                transition: "all 0.3s ease"
              }}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <UilMessage size="18" />
              )}
            </Button>
          </div>
        </div>
                          {error && <small className="text-danger mt-2 d-block">{formatErrorForReact(error)}</small>}
      </div>
    </div>
  );

  const renderConversationHistory = () => (
    <div className="conversation-history modern-history">
      <div className="history-header d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <div className="icon-wrapper bg-gradient-secondary me-3">
            <UilHistory className="text-white" size="18" />
          </div>
          <div>
            <h6 className="text-body-highlight mb-0 fw-semibold">Conversation History</h6>
            <small className="text-muted">Your complete chat timeline</small>
          </div>
        </div>
        <Button 
          variant="link" 
          size="sm" 
          className="refresh-btn p-2 rounded-circle"
          onClick={loadConversationHistory}
        >
          <UilRefresh size="16" />
        </Button>
      </div>
      
      <div className="history-container">
        {conversationHistory.length > 0 ? (
          <div className="history-timeline">
            {conversationHistory.map((message, index) => (
              <div
                key={message.id}
                className={classNames(
                  "history-message-item",
                  message.sender === "user" ? "user-history-message" : "ai-history-message"
                )}
              >
                <div className="message-avatar">
                  {message.sender === "ai" ? (
                    <div className="avatar-circle bg-primary">
                      <UilBolt className="text-white" size="14" />
                    </div>
                  ) : (
                    <div className="avatar-circle bg-secondary">
                      <UilUser className="text-white" size="14" />
                    </div>
                  )}
                </div>
                
                <div className="message-content-wrapper">
                  <div className="message-header d-flex align-items-center justify-content-between mb-2">
                    <div className="sender-info d-flex align-items-center">
                      <span className="sender-name fw-semibold fs-9">
                        {message.sender === "ai" ? "Kairos AI" : "You"}
                      </span>
                      <div className="sender-badge ms-2">
                        {message.sender === "ai" ? (
                          <Badge bg="primary" className="fs-11">AI</Badge>
                        ) : (
                          <Badge bg="secondary" className="fs-11">You</Badge>
                        )}
                      </div>
                    </div>
                    <small className="message-timestamp text-muted">
                      {message.timestamp 
                        ? new Date(message.timestamp).toLocaleString([], { 
                            month: 'short', 
                            day: 'numeric', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })
                        : 'Recently'
                      }
                    </small>
                  </div>
                  
                  <div className="message-bubble-history">
                    <p className="message-text mb-0">{message.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-history-state">
            <div className="empty-icon-wrapper mb-3">
              <div className="empty-icon bg-light rounded-circle d-flex align-items-center justify-content-center">
                <UilHistory size="32" className="text-muted opacity-50" />
              </div>
            </div>
            <div className="empty-content text-center">
              <h6 className="text-muted mb-2">No Conversation History</h6>
              <p className="text-muted fs-9 mb-3">Start chatting with the AI to build your conversation history</p>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => setActiveTab("chat")}
              >
                Start Chat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderLeadEssentials = () => (
    <div style={{ padding: '20px' }}>
      {/* Clean Lead Header */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '18px' }}>
              {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
            </h5>
            <div className="d-flex align-items-center gap-2">
              {leadScore && (
                <Badge 
                  bg={leadScore.score >= 80 ? 'success' : leadScore.score >= 60 ? 'warning' : 'primary'}
                  style={{ fontSize: '11px' }}
                >
                  {leadScore.score}/100
                </Badge>
              )}
              <Badge 
                bg={leadDetails?.lead_stage === 'Hot' ? 'danger' : leadDetails?.lead_stage === 'Warm' ? 'warning' : 'secondary'}
                style={{ fontSize: '11px' }}
              >
                {leadDetails?.lead_stage || 'New'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Essential Contact Info */}
        <div className="mb-4">
          <div className="mb-2">
            <a 
              href={`mailto:${leadDetails?.lead_email}`}
              className="text-primary text-decoration-none d-flex align-items-center"
              style={{ fontSize: '14px' }}
            >
              <UilEnvelope size="16" className="me-2 text-muted" />
              {leadDetails?.lead_email}
            </a>
          </div>
          <div className="mb-3">
            <a 
              href={`tel:${leadDetails?.lead_phone}`}
              className="text-primary text-decoration-none d-flex align-items-center"
              style={{ fontSize: '14px' }}
            >
              <UilPhone size="16" className="me-2 text-muted" />
              {leadDetails?.lead_phone}
            </a>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="d-flex gap-2 mb-4">
          <Button
            variant="success"
            className="flex-fill"
            onClick={() => window.open(`tel:${leadDetails?.lead_phone}`, '_self')}
            style={{ fontSize: '13px', padding: '10px', fontWeight: 500 }}
          >
            <UilPhone size="16" className="me-1" />
            Call
          </Button>
          <Button
            variant="primary"
            className="flex-fill"
            onClick={() => window.open(`mailto:${leadDetails?.lead_email}`, '_blank')}
            style={{ fontSize: '13px', padding: '10px', fontWeight: 500 }}
          >
            <UilEnvelope size="16" className="me-1" />
            Email
          </Button>
        </div>

        {/* Essential Details */}
        <div style={{ 
          background: 'rgba(248, 250, 252, 0.8)', 
          borderRadius: '8px', 
          padding: '16px',
          border: '1px solid rgba(0, 0, 0, 0.05)' 
        }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted" style={{ fontSize: '13px' }}>Budget:</span>
            <span className="fw-medium" style={{ fontSize: '13px' }}>
              {leadDetails?.lead_budget ? formatCurrency(leadDetails.lead_budget) : 'Not specified'}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted" style={{ fontSize: '13px' }}>Location:</span>
            <span className="fw-medium" style={{ fontSize: '13px' }}>
              {leadDetails?.lead_location || 'Not specified'}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted" style={{ fontSize: '13px' }}>Source:</span>
            <span style={{ fontSize: '13px' }}>{leadDetails?.lead_source || 'Direct'}</span>
          </div>
        </div>
      </div>

      {/* Quick Tools */}
      <div className="mt-4">
        <h6 className="mb-3 fw-semibold text-muted" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Quick Actions
        </h6>
        <div className="d-grid gap-2">
          <Button
            variant="outline-primary"
            className="text-start"
            style={{ fontSize: '13px', padding: '10px 16px', border: '1px solid #e5e7eb' }}
          >
            <UilCalendarAlt size="16" className="me-2" />
            Schedule Meeting
          </Button>
          <Button
            variant="outline-primary"
            className="text-start"
            style={{ fontSize: '13px', padding: '10px 16px', border: '1px solid #e5e7eb' }}
          >
            <UilHome size="16" className="me-2" />
            Find Properties
          </Button>
        </div>
      </div>

      {/* Back Button */}
      <div className="mt-4 text-center">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setActiveTab('insights')}
          style={{ fontSize: '12px', padding: '8px 20px' }}
        >
          <UilRefresh size="14" className="me-1" />
          Back to Insights
        </Button>
      </div>
    </div>
  );

  const renderWorkflowDetails = () => (
    <div 
      className="insight-card mb-4"
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        backdropFilter: 'blur(10px)',
        padding: '20px',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.1)'
      }}
    >
      <div className="card-header d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <div className="icon-wrapper bg-gradient-primary me-3">
            <UilProcess className="text-white" size="18" />
          </div>
          <div>
            <h6 className="text-body-highlight mb-0 fw-semibold">Workflow Status</h6>
            <small className="text-muted">Current stage and next actions</small>
          </div>
        </div>
        <Button 
          variant="link" 
          size="sm" 
          className="p-0"
          onClick={() => setIsWorkflowExpanded(!isWorkflowExpanded)}
        >
          <UilAngleDown 
            size="20" 
            style={{ 
              transform: isWorkflowExpanded ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.3s ease'
            }} 
          />
        </Button>
      </div>

      {isWorkflowExpanded && (
        <div className="workflow-content">
          {/* Current Stage */}
          <div className="workflow-stage mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-medium">Current Stage</span>
              <Badge bg="primary">Property Discovery</Badge>
            </div>
            <div className="progress" style={{ height: '4px' }}>
              <div 
                className="progress-bar bg-primary" 
                style={{ 
                  width: '35%',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                }}
              ></div>
            </div>
          </div>

          {/* Next Actions */}
          <div className="next-actions mb-3">
            <h6 className="fw-medium mb-2">Next Actions</h6>
            <div className="action-list">
              <div className="action-item d-flex align-items-center mb-2">
                <div 
                  className="action-dot me-2"
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#10b981'
                  }}
                ></div>
                <span className="text-muted">Review collected preferences</span>
              </div>
              <div className="action-item d-flex align-items-center mb-2">
                <div 
                  className="action-dot me-2"
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#f59e0b'
                  }}
                ></div>
                <span className="text-muted">Setup property search criteria</span>
              </div>
            </div>
          </div>

          {/* Recent Transitions */}
          <div className="recent-transitions">
            <h6 className="fw-medium mb-2">Recent Activity</h6>
            <div className="transition-list">
              <div className="transition-item d-flex align-items-center mb-2">
                <UilCheckCircle className="text-success me-2" size="14" />
                <small className="text-muted">Completed initial consultation</small>
              </div>
              <div className="transition-item d-flex align-items-center">
                <UilCheckCircle className="text-success me-2" size="14" />
                <small className="text-muted">Property preferences collected</small>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Panel renderers
  function renderLeadScorePanel() {
    return (
      leadScore && (
        <div className="insight-card mb-4" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', border: '1px solid rgba(0, 212, 255, 0.2)', backdropFilter: 'blur(10px)', padding: '16px 18px', boxShadow: '0 4px 20px rgba(0, 212, 255, 0.1)' }}>
          <div className="card-header d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <div className="icon-wrapper bg-gradient-primary me-3">
                {getScoreIcon(leadScore.score)}
              </div>
              <div>
                <h6 className="text-body-highlight mb-0 fw-semibold">Lead Score</h6>
                <small className="text-muted">AI-powered lead assessment</small>
              </div>
            </div>
          </div>
          <hr style={{ margin: '12px 0' }} />
          <div className="d-flex align-items-center mb-2">
            <span style={{ fontSize: '2.2rem', fontWeight: 700, color: '#2563eb', marginRight: 8 }}>{leadScore.score}</span>
            <span style={{ fontSize: '1.1rem', color: '#6b7280', marginRight: 8 }}>/100</span>
            <span className="badge" style={{ background: leadScore.score >= 80 ? '#10b981' : leadScore.score >= 60 ? '#f59e0b' : '#3b82f6', color: '#fff', fontWeight: 600, borderRadius: 12, padding: '4px 12px', fontSize: '0.95rem' }}>{leadScore.priority.toUpperCase()}</span>
          </div>
          <div className="progress mb-3" style={{ height: '8px', background: '#f3f4f6' }}>
            <div className="progress-bar" style={{ width: `${leadScore.score}%`, background: leadScore.score >= 80 ? '#10b981' : leadScore.score >= 60 ? '#f59e0b' : '#2563eb' }}></div>
          </div>
          <div>
            <div className="fw-semibold mb-2" style={{ fontSize: '1rem' }}>Key Factors</div>
            {leadScore.factors && leadScore.factors.map((factor, idx) => (
              <div key={idx} className="d-flex align-items-center mb-1" style={{ fontSize: '0.98rem' }}>
                <span style={{ minWidth: 120 }}>{factor.factor}</span>
                <span className="badge me-2" style={{ background: factor.impact === 'positive' ? '#10b981' : factor.impact === 'negative' ? '#ef4444' : '#374151', color: '#fff', fontWeight: 500, borderRadius: 8, padding: '2px 10px', fontSize: '0.85rem' }}>{factor.impact}</span>
                <span style={{ color: '#6b7280', fontWeight: 500 }}>{factor.weight}%</span>
              </div>
            ))}
          </div>
        </div>
      )
    );
  }
  function renderAIInsightsPanel() {
    // Only show the AI Insights card from renderInsightsOverview
    return (
      <>
        {leadAIContext && (
          <div className="insight-card mb-4" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', border: '1px solid rgba(245, 158, 11, 0.2)', backdropFilter: 'blur(10px)', padding: '20px', boxShadow: '0 4px 20px rgba(245, 158, 11, 0.1)' }}>
            <div className="card-header d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <div className="icon-wrapper bg-gradient-success me-3">
                  <UilBrain className="text-white" size="18" />
                </div>
                <div>
                  <h6 className="text-body-highlight mb-0 fw-semibold">AI Insights</h6>
                  <small className="text-muted">Claude 4 analysis results</small>
                </div>
              </div>
              <Button variant="link" size="sm" className="refresh-btn p-2 rounded-circle" onClick={() => analyzeLeadWithClaude4('comprehensive')} disabled={aiAnalysisLoading}>
                {aiAnalysisLoading ? <Spinner animation="border" size="sm" /> : <UilRefresh size="16" />}
              </Button>
            </div>
            <div className="card-content">
              {leadAIContext.key_insights && (
                <div className="key-insights mb-3">
                  <h6 className="section-title mb-2">Key Insights</h6>
                  <div className="insights-text">
                    <p className="fs-9 text-muted mb-0">{leadAIContext.key_insights}</p>
                  </div>
                </div>
              )}
              {leadAIContext.ai_recommendations && (
                <div className="ai-recommendations mb-3">
                  <h6 className="section-title mb-2">Recommendations</h6>
                  <div className="recommendations-text">
                    <p className="fs-9 text-muted mb-0">{leadAIContext.ai_recommendations}</p>
                  </div>
                </div>
              )}
              {leadAIContext.last_ai_analysis && (
                <div className="analysis-meta">
                  <small className="text-muted">Last analysis: {new Date(leadAIContext.last_ai_analysis).toLocaleString()}</small>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  const INSIGHTS_PANELS = [
    { key: 'workflow', label: 'Workflow Status', render: renderWorkflowDetails },
    { key: 'score', label: 'Lead Score', render: renderLeadScorePanel },
    { key: 'ai', label: 'AI Insights', render: renderAIInsightsPanel }
  ];

  return (
    <Card 
      className={classNames(className)} 
      style={{ 
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '20px',
        overflow: 'hidden'
      }}
    >
      <Card.Body style={{ padding: 0 }}>
        <div className={classNames("enhanced-lead-insights ai-forward-container", className)}>
          {/* Dynamic Header - Changes based on active tab */}
          <div 
            style={{
              position: 'relative',
              padding: '24px',
              background: activeTab === 'chat' 
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
              borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ 
              position: 'relative', 
              zIndex: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ position: 'relative', marginRight: '16px' }}>
                  {activeTab === 'chat' ? (
                    <UilMessage 
                      size="32" 
                      style={{ 
                        color: '#10b981', 
                        filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.5))' 
                      }} 
                    />
                  ) : (
                    <UilBrain 
                      size="32" 
                      style={{ 
                        color: '#00d4ff', 
                        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))' 
                      }} 
                    />
                  )}
                </div>
                <div>
                  <h5 style={{ 
                    color: '#1f2937', 
                    fontWeight: 600, 
                    marginBottom: 0,
                    textShadow: activeTab === 'chat' 
                      ? '0 0 10px rgba(16, 185, 129, 0.2)'
                      : '0 0 10px rgba(0, 212, 255, 0.2)' 
                  }}>
                    {activeTab === 'chat' ? 'Lead Essentials & Actions' : 'AI Lead Intelligence'}
                  </h5>
                  <small style={{ 
                    color: '#6b7280', 
                    fontSize: '12px', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.5px' 
                  }}>
                    {activeTab === 'chat' ? 'Quick access tools & contact info' : 'Neural network-powered insights'}
                  </small>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: '1' }}>
                <Badge 
                  style={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <UilBolt size="12" className="me-1" />
                  Enhanced
                </Badge>
                
                {aiEligibility?.eligible && (
                  <Badge 
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <UilRobot size="12" className="me-1" />
                    Consumer AI
                  </Badge>
                )}
                
                {leadScore && (
                  <Badge 
                    style={{
                      background: `linear-gradient(135deg, ${
                        leadScore.score >= 80 ? 'rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)' :
                        leadScore.score >= 60 ? 'rgba(245, 158, 11, 0.8) 0%, rgba(217, 119, 6, 0.8) 100%)' :
                        leadScore.score >= 40 ? 'rgba(59, 130, 246, 0.8) 0%, rgba(37, 99, 235, 0.8) 100%)' :
                        'rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)'
                      }`,
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <UilChart size="12" className="me-1" />
                    Score: {leadScore.score}
                  </Badge>
                )}
                
              {leadDetails?.lead_type?.lead_type === "Buyer" && (
                  <Badge 
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.8) 0%, rgba(79, 70, 229, 0.8) 100%)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <UilUser size="12" className="me-1" />
                    Buyer
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {userInfo?.user_subscription?.subscription_package?.subscription_package_name !== "Essential" || true ? (
            <div style={{ position: 'relative' }}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || "insights")}
                className="mb-0"
                fill
                style={{
                  borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
                }}
              >
                <Tab 
                  eventKey="insights" 
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <UilBrain size="16" className="me-2" style={{ color: '#00d4ff' }} />
                      <span>Insights</span>
                    </div>
                  }
                >
                  <div style={{ padding: '12px 0 0 0', minHeight: '400px', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                      <Button variant="link" className="p-2" onClick={handlePrevPanel} aria-label="Previous Panel">
                        <UilArrowLeft size="20" />
                      </Button>
                      <div style={{ flex: 1, textAlign: 'center', fontWeight: 500, color: '#00d4ff', letterSpacing: '0.5px', fontSize: '1.1rem' }}>
                        {INSIGHTS_PANELS[insightsPanelIndex].label} <span style={{ fontSize: '12px', color: '#6b7280' }}>({insightsPanelIndex + 1} / {INSIGHTS_PANELS.length})</span>
                      </div>
                      <Button variant="link" className="p-2" onClick={handleNextPanel} aria-label="Next Panel">
                        <UilArrowRight size="20" />
                      </Button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '95%', maxWidth: 400 }}>
                        {INSIGHTS_PANELS[insightsPanelIndex].render()}
                      </div>
                    </div>
                  </div>
              </Tab>
                <Tab 
                  eventKey="chat" 
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <UilMessage size="16" className="me-2" style={{ color: '#00d4ff' }} />
                      <span>AI Chat</span>
                    </div>
                  }
                >
                  <div style={{ padding: '16px', minHeight: '400px' }}>
                    {renderLeadEssentials()}
                  </div>
              </Tab>
                <Tab 
                  eventKey="history" 
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <UilHistory size="16" className="me-2" style={{ color: '#00d4ff' }} />
                      <span>History</span>
                    </div>
                  }
                >
                  <div style={{ padding: '24px', minHeight: '400px' }}>
                {renderConversationHistory()}
                  </div>
              </Tab>
            </Tabs>
            </div>
          ) : (
            <div style={{ 
              padding: '48px 24px', 
              textAlign: 'center', 
              background: 'radial-gradient(circle at center, rgba(0, 212, 255, 0.1) 0%, transparent 70%)' 
            }}>
              <UilBrain 
                size="48" 
                style={{ 
                  color: '#00d4ff', 
                  filter: 'drop-shadow(0 0 12px rgba(0, 212, 255, 0.3))',
                  marginBottom: '16px'
                }} 
              />
              <h6 style={{ color: '#1f2937', fontWeight: 600, marginBottom: '12px' }}>
                AI Intelligence Locked
              </h6>
              <p style={{ 
                color: '#6b7280', 
                marginBottom: '24px', 
                maxWidth: '400px', 
                marginLeft: 'auto', 
                marginRight: 'auto' 
              }}>
                Unlock neural network-powered lead insights with an upgraded subscription.
              </p>
              <Button
                as={Link}
                to="/settings/subscription-settings"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '25px',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s ease'
                }}
              >
                <UilBolt size="16" className="me-2" />
                Upgrade to AI Pro
              </Button>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default EnhancedLeadInsights; 